'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import {
  BillingAddressEnum,
  MailingAddressEnum,
} from '@sola/schema/dotcom/shared/address.ts';
import {
  COUNTRY_CODE_US,
  CountryCode,
  CountryEnum,
  StateEnum,
} from '@sola/schema/dotcom/shared/geo.ts';

import { InputField } from '../input-field/input-field.tsx';
import { PostalCodeField } from '../postal-code-field/postal-code-field.tsx';
import { Select } from '../select/select.tsx';
import { StateField } from '../state-field/state-field.tsx';

const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));
const countryOptions = Object.entries(CountryEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));

export interface AddressFieldsProps {
  addressType: CheckoutAddressTypeEnum;
  /**
   * Used only when country needs to be controlled.
   */
  countryValue?: CountryCode;
  defaultCountry?: CountryCode;
  onCountryChange?: (countryCode: CountryCode) => void;
  shouldUnregister?: boolean;
  shouldValidate?: boolean;
}

export const AddressFields: React.FC<AddressFieldsProps> = ({
  addressType,
  countryValue,
  defaultCountry,
  onCountryChange,
  shouldUnregister,
  shouldValidate = true,
}) => {
  const { t } = useTranslation(['checkout', 'forms']);
  const [countryState, setCountryState] = useState(defaultCountry);
  const selectedCountry = countryValue || countryState;
  const addressLabelToken = `${addressType}AddressLabel`;
  const autoCompleteGroup =
    addressType === CheckoutAddressTypeEnum.Mailing ? '' : 'billing '; // The space after billing is NECESSARY - it denotes an autocomplete group
  const FieldGroupEnum =
    addressType === CheckoutAddressTypeEnum.Mailing
      ? MailingAddressEnum
      : BillingAddressEnum;
  const isUS = selectedCountry === COUNTRY_CODE_US;

  return (
    <>
      <InputField
        label={t(`forms:addressFields.${addressLabelToken}`)}
        name={FieldGroupEnum.Address}
        autoComplete={`${autoCompleteGroup}address-line1`}
        required={shouldValidate}
        removeCardNumbers={{
          message: t('forms:errors.addressNumberError'),
        }}
        pattern={{
          value:
            /^$|^(?![\w!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,63}))/,
          message: t('forms:errors.addressNumberError'),
        }}
        shouldUnregister={shouldUnregister ? true : undefined}
      />

      <InputField
        label={t('forms:addressFields.addressAdditionalLabel')}
        name={FieldGroupEnum.Address2}
        autoComplete={`${autoCompleteGroup}address-line2`}
        removeCardNumbers={{
          message: t('forms:errors.addressNumberError'),
        }}
        shouldUnregister={shouldUnregister ? true : undefined}
      />
      <Select
        label={t('forms:addressFields.countryLabel')}
        placeholder={t('forms:addressFields.countryPlaceholder')}
        defaultValue={defaultCountry}
        name={FieldGroupEnum.Country}
        autoComplete={`${autoCompleteGroup}country`}
        required={shouldValidate}
        items={countryOptions}
        shouldUnregister={shouldUnregister ? true : undefined}
        onChange={(e) => {
          const { value } = e.target;
          if (onCountryChange) {
            onCountryChange(value);
          } else {
            setCountryState(value);
          }
        }}
        {...(countryValue && { value: countryValue })}
      />

      <div className="flex gap-comfy-sm flex-col lg:flex-row">
        <InputField
          label={t('forms:addressFields.cityLabel')}
          name={FieldGroupEnum.City}
          fieldClasses="flex-1"
          autoComplete={`${autoCompleteGroup}address-level2`}
          autoCorrect="off"
          spellCheck={false}
          required={shouldValidate}
          removeCardNumbers={{
            message: t('forms:errors.cityNumberError'),
          }}
          shouldUnregister={shouldUnregister ? true : undefined}
        />

        <StateField
          label={
            isUS
              ? t('forms:addressFields.stateLabel')
              : t('forms:addressFields.provinceLabel')
          }
          name={FieldGroupEnum.State}
          fieldClasses="flex-1"
          autoComplete={`${autoCompleteGroup}address-level1`}
          spellCheck={false}
          removeCardNumbers={{
            message: t('forms:errors.provinceNumberError'),
          }}
          placeholder={isUS ? t('forms:addressFields.statePlaceholder') : ''}
          items={stateOptions}
          required={shouldValidate && isUS}
          shouldUnregister={shouldUnregister ? true : undefined}
          isUS={isUS}
        />

        <PostalCodeField
          label={
            isUS
              ? t('forms:addressFields.zipCodeLabel')
              : t('forms:addressFields.postalCodeLabel')
          }
          name={FieldGroupEnum.Postal}
          fieldClasses="flex-1"
          autoComplete={`${autoCompleteGroup}postal-code`}
          shouldUnregister={shouldUnregister ? true : undefined}
          countryCode={selectedCountry}
          shouldValidate={shouldValidate}
        />
      </div>
    </>
  );
};
